import { useEffect, useState } from "react";
import { Building2, User, Lock, Save } from "lucide-react";
import { GetSettings, UpdateSettings } from "../api";

function Settings() {
  const [settings, setSettings] = useState({
    name: "",
    companyName: "",
    gstNumber: "",
    email: "",
    phone: "",
    website: "",
    profileImage: null,
    profilePreview: "",
    oldPassword: "",
    newPassword: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);
  const IMAGE_URL =
    process.env.NODE_ENV === "production"
      ? "https://polybionictechnologies.com"
      : "http://localhost:5001";

  const fetchSettings = async () => {
    try {
      const res = await GetSettings();

      if (res.data.success) {
        const data = res.data.settings;

        setSettings((prev) => ({
          ...prev,
          name: data.name || "",
          companyName: data.companyName || "",
          gstNumber: data.gstNumber || "",
          email: data.email || "",
          phone: data.phone || "",
          website: data.website || "",
          profilePreview: data.profileImage
            ? `${IMAGE_URL}${data.profileImage}`
            : "",
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value,
    });
  };
  const handleImage = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setSettings((prev) => ({
      ...prev,
      profileImage: file,
      profilePreview: URL.createObjectURL(file),
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      formData.append("name", settings.name);
      formData.append("companyName", settings.companyName);
      formData.append("gstNumber", settings.gstNumber);
      formData.append("email", settings.email);
      formData.append("phone", settings.phone);
      formData.append("website", settings.website);

      if (settings.profileImage) {
        formData.append("profileImage", settings.profileImage);
      }

      if (settings.oldPassword) {
        formData.append("oldPassword", settings.oldPassword);
      }

      if (settings.newPassword) {
        formData.append("newPassword", settings.newPassword);
      }

      const res = await UpdateSettings(formData);

      if (res.data.success) {
        alert("Profile Updated Successfully");
        fetchSettings();
      }
    } catch (error) {
      console.log(error);
      alert(error.response?.data?.message || "Update Failed");
    }
  };
  return (
    <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 px-3 sm:px-4 md:px-0">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Settings
          </h1>

          <p className="text-sm md:text-base text-gray-500 mt-1">
            Manage your company and administrator profile
          </p>
        </div>

        <button
          type="submit"
          form="settingsForm"
          className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center justify-center gap-2 shadow"
        >
          <Save size={18} />
          Save Changes
        </button>
      </div>

      <form
        id="settingsForm"
        onSubmit={handleSubmit}
        className="space-y-4 md:space-y-6 mb-6"
      >
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow p-4 md:p-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="flex flex-col items-center w-full lg:w-auto">
              <img
                src={settings.profilePreview || "/default-user.png"}
                alt="Profile"
                className="w-28 h-28 sm:w-32 sm:h-32 rounded-full border-4 border-blue-100 object-cover"
              />

              <input
                type="file"
                accept="image/*"
                onChange={handleImage}
                className="mt-4 w-full text-sm file:bg-blue-600 file:text-white file:border-0 file:px-4 file:py-2 file:rounded-lg file:cursor-pointer hover:file:bg-blue-700 transition"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 flex-1 w-full">
              <div>
                <label className="block mb-2 font-medium">User Name</label>

                <input
                  type="text"
                  name="name"
                  value={settings.name}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Email</label>

                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block mb-2 font-medium">Phone Number</label>

                <input
                  type="text"
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                  className="w-full border rounded-lg p-3"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div className="bg-white rounded-2xl shadow p-4 md:p-6">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="text-blue-600" size={22} />
            <h2 className="text-lg md:text-xl font-semibold">
              Company Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block mb-2 font-medium">Company Name</label>

              <input
                type="text"
                name="companyName"
                value={settings.companyName}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium">Website</label>

              <input
                type="text"
                name="website"
                value={settings.website}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block mb-2 font-medium">GST Number</label>

              <input
                type="text"
                name="gstNumber"
                value={settings.gstNumber}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl shadow p-4 mb-6 md:p-6">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="text-red-500" size={22} />
            <h2 className="text-lg md:text-xl font-semibold">
              Change Password
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block mb-2 font-medium">Current Password</label>

              <input
                type="password"
                name="oldPassword"
                value={settings.oldPassword}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium">New Password</label>

              <input
                type="password"
                name="newPassword"
                value={settings.newPassword}
                onChange={handleChange}
                className="w-full border rounded-lg p-3"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Settings;
